import { useForm } from 'react-hook-form';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { useCreateAlertRule, useUpdateAlertRule } from '@/hooks/queries/useAlerts';
import { alertTypeDisplayNames } from '@/lib/formatters';
import type { AlertRule, AlertType } from '@steward/shared';

interface AlertRuleFormProps {
  rule?: AlertRule | null;
  onSuccess: () => void;
  onCancel: () => void;
}

interface FormValues {
  name: string;
  alertType: AlertType;
  isEnabled: boolean;
  thresholdPercent: number | '';
  daysThreshold: number | '';
  strikeProximityPercent: number | '';
  extrinsicThreshold: number | '';
  symbols: string;
  cooldownMinutes: number;
}

const alertTypeOptions = Object.entries(alertTypeDisplayNames).map(([value, label]) => ({
  value,
  label,
}));

export function AlertRuleForm({ rule, onSuccess, onCancel }: AlertRuleFormProps) {
  const createRule = useCreateAlertRule();
  const updateRule = useUpdateAlertRule();
  const isEditing = !!rule;

  const { register, handleSubmit, watch, formState: { errors } } = useForm<FormValues>({
    defaultValues: {
      name: rule?.name || '',
      alertType: rule?.alertType || 'profit_target',
      isEnabled: rule?.isEnabled ?? true,
      thresholdPercent: rule?.conditions.thresholdPercent ?? '',
      daysThreshold: rule?.conditions.daysThreshold ?? '',
      strikeProximityPercent: rule?.conditions.strikeProximityPercent ?? '',
      extrinsicThreshold: rule?.conditions.extrinsicThreshold ?? '',
      symbols: rule?.conditions.symbols?.join(', ') || '',
      cooldownMinutes: rule?.cooldownMinutes || 60,
    },
  });

  const watchedType = watch('alertType');

  const onSubmit = async (data: FormValues) => {
    const conditions: AlertRule['conditions'] = {};

    if (data.thresholdPercent !== '') {
      conditions.thresholdPercent = Number(data.thresholdPercent);
    }
    if (data.daysThreshold !== '') {
      conditions.daysThreshold = Number(data.daysThreshold);
    }
    if (data.strikeProximityPercent !== '') {
      conditions.strikeProximityPercent = Number(data.strikeProximityPercent);
    }
    if (data.extrinsicThreshold !== '') {
      conditions.extrinsicThreshold = Number(data.extrinsicThreshold);
    }
    if (data.symbols) {
      conditions.symbols = data.symbols.split(',').map((s) => s.trim().toUpperCase()).filter(Boolean);
    }

    if (isEditing && rule) {
      await updateRule.mutateAsync({
        id: rule.id,
        data: {
          name: data.name,
          alertType: data.alertType,
          isEnabled: data.isEnabled,
          conditions,
          cooldownMinutes: data.cooldownMinutes,
        },
      });
    } else {
      await createRule.mutateAsync({
        name: data.name,
        alertType: data.alertType,
        isEnabled: data.isEnabled,
        conditions,
        cooldownMinutes: data.cooldownMinutes,
      });
    }

    onSuccess();
  };

  const showThreshold = ['profit_target', 'loss_limit'].includes(watchedType);
  const showDays = ['roll_opportunity', 'expiration_warning'].includes(watchedType);
  const showProximity = watchedType === 'strike_proximity';
  const showExtrinsic = watchedType === 'early_assignment_risk';

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <Input
        label="Rule Name"
        placeholder="My alert rule"
        {...register('name', { required: 'Name is required' })}
        error={errors.name?.message}
      />

      <Select
        label="Alert Type"
        options={alertTypeOptions}
        {...register('alertType')}
      />

      {showThreshold && (
        <Input
          label="Threshold Percent"
          type="number"
          placeholder="50"
          hint="Percentage threshold to trigger alert"
          {...register('thresholdPercent', { valueAsNumber: true })}
        />
      )}

      {showDays && (
        <Input
          label="Days Threshold"
          type="number"
          placeholder="21"
          hint="Number of days to trigger alert"
          {...register('daysThreshold', { valueAsNumber: true })}
        />
      )}

      {showProximity && (
        <Input
          label="Strike Proximity Percent"
          type="number"
          placeholder="5"
          hint="Percentage distance from strike price"
          {...register('strikeProximityPercent', { valueAsNumber: true })}
        />
      )}

      {showExtrinsic && (
        <Input
          label="Extrinsic Threshold ($)"
          type="number"
          step="0.01"
          placeholder="0.10"
          hint="Alert when a short ITM leg's extrinsic (time) value is at or below this dollar amount (default 0.10)"
          {...register('extrinsicThreshold', { valueAsNumber: true })}
        />
      )}

      <Input
        label="Symbols (optional)"
        placeholder="SPY, AAPL, TSLA"
        hint="Comma-separated list of symbols. Leave empty for all."
        {...register('symbols')}
      />

      <Input
        label="Cooldown (minutes)"
        type="number"
        min={5}
        {...register('cooldownMinutes', { valueAsNumber: true, min: 5 })}
        hint="Minimum time between alerts"
      />

      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="isEnabled"
          className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
          {...register('isEnabled')}
        />
        <label htmlFor="isEnabled" className="text-sm text-gray-700">
          Enable this rule
        </label>
      </div>

      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type="submit"
          isLoading={createRule.isPending || updateRule.isPending}
        >
          {isEditing ? 'Update Rule' : 'Create Rule'}
        </Button>
      </div>
    </form>
  );
}
